import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { PortfolioService } from '@shared/services/portfolio.service';
import { IPortfolio } from '@static/models/portfolio';
import { PortfolioAddComponent } from '../portfolio-add/portfolio-add.component';
import { ObservableService } from '@static/services/observable.service';
import { MatCardModule } from '@angular/material/card';
import { ConfirmComponent } from '@shared/dialogs/confirm/confirm.component';
import { AuthService } from '@static/services/auth.service';
import { IProject } from '@static/models/project';
import { ProjectService } from '@shared/services/project.service';
import { FillHeightDirective } from "@static/directives/fill-height.directive";
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-project-portfolio',
  imports: [
    MatIconModule,
    MatButtonModule,
    MatCardModule,
    MatTooltipModule,
    FillHeightDirective,
  ],
  templateUrl: './project-portfolio.component.html',
  styleUrl: './project-portfolio.component.scss',
  providers: [
    ObservableService,
  ]
})
export class ProjectPortfolioComponent {
  private _observableService: ObservableService = inject(ObservableService);
  private _authService: AuthService = inject(AuthService);
  protected _portfolioService: PortfolioService = inject(PortfolioService);
  protected _projectService: ProjectService = inject(ProjectService);
  private _dialog: MatDialog = inject(MatDialog);

  protected portfolioMap: Map<number, string[]>;
  protected portfolios: IPortfolio[];
  protected isAdmin: boolean;

  constructor() {
    this.portfolios = [];
    this.portfolioMap = new Map<number, string[]>();

    this._observableService.subscribe(
      this._portfolioService.portfoliosUpdated,
      () => {
        this.portfolios = this._portfolioService.portfolios;
      }
    );

    this._observableService.subscribe(
      this._projectService.projectListUpdated,
      () => this._setPortfolioMap()
    );

    this._setPortfolioMap();
    this._portfolioService.loadPortfolios();
    this.isAdmin = this._authService.isSiteAdmin;
  }

  /**
   * Set the portfolio project map
   */
  private _setPortfolioMap(): void {
    this.portfolioMap.clear();
    this._projectService.projectList.forEach((proj: IProject) => {
      proj.portfolios.forEach((port: number) => {
        if (this.portfolioMap.has(port)) { this.portfolioMap.get(port)!.push(`${proj.proj_full_name}`) }
        else this.portfolioMap.set(port, [`${proj.proj_full_name}`])
      });
    });
  }

  /**
   * Create a new portfolio
   */
  public newPortfolio(): void {
    this._dialog.open(PortfolioAddComponent, {
      minWidth: '500px',
    });
  }

  /**
   * Edit an existing portfolio
   *
   * @param data  portfolio data
   */
  public editPortfolio(data: IPortfolio): void {
    const selectedProjIds = this._projectService.projectList.filter(
      (p) => p.portfolios.includes(data.id)
    ).map((p) => p.proj_id);
    (data as IPortfolio).projects = selectedProjIds;
    this._dialog.open(PortfolioAddComponent, {
      data,
      minWidth: '500px',
    });
  }

  /**
   * Delete an existing portfolio
   *
   * @param data  portfolio to remove
   */
  public deletePortfolio(data: IPortfolio): void {
    const msg = `Are you sure you want to delete '${data.name}'?`;
    const confirm = this._dialog.open(ConfirmComponent, { data: msg });

    this._observableService.subscribe(
      confirm.afterClosed(),
      (res: boolean) => {
        if (res) {
          this._portfolioService.deletePortfolio(data.id);
        }
      }
    );
  }

  /**
   * Select the portfolio
   *
   * @param id  portfolio ID
   */
  public selectPortfolio(id: number): void {
    this._portfolioService.setSelected(id);
  }
}
